import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Grid,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Settings as SettingsIcon,
  Add,
  Edit,
  Delete,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import Swal from 'sweetalert2';
import apiService from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

interface Module {
  id: number;
  name: string;
  display_name: string;
  description: string;
  icon: string;
  route: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

const Modules: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const response = await apiService.getModules();
      setModules((response.data as any).data || response.data || []);
    } catch (error: any) {
      console.error('Error fetching modules:', error);
      toast.error(error.message || 'Failed to fetch modules');
    } finally {
      setLoading(false);
    }
  };

  const filteredModules = modules.filter(module =>
    module.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    module.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (module.description && module.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedModules = filteredModules.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredModules.length - page * rowsPerPage);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedModules.map((module) => module.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleDeleteModule = async (moduleId: number) => {
    const module = modules.find(m => m.id === moduleId);
    const moduleName = module ? module.display_name : 'this module';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${moduleName}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteModule(moduleId);
        
        Swal.fire({
          title: 'Deleted!',
          text: 'Module has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
        
        fetchModules(); // Refresh the modules list
      } catch (error: any) {
        console.error('Error deleting module:', error);
        
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to delete module',
          icon: 'error',
          confirmButtonColor: '#3085d6',
        });
      }
    }
  };

  const getModuleStatusColor = (isActive: boolean) => {
    return isActive ? '#10b981' : '#ef4444';
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      <ToastContainer />
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/modules/add')}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Module
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Settings Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    Total Modules
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '2rem'
                  }}>
                    {modules.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    Active Modules
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '2rem'
                  }}>
                    {modules.filter(module => module.is_active).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    Inactive Modules
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '2rem'
                  }}>
                    {modules.filter(module => !module.is_active).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Filter Section */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            gap: 2,
            mb: 3,
            p: 3,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
              <TextField
                placeholder="Search modules..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{
                  borderRadius: '8px',
                  height: '48px',
                  px: 2,
                  borderColor: '#d1d5db',
                  color: '#374151',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                Filter
              </Button>
            </Box>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              sx={{
                borderRadius: '8px',
                height: '48px',
                px: 2,
                borderColor: '#d1d5db',
                color: '#374151',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb',
                },
              }}
            >
              Export
            </Button>
          </Box>

          {/* Modules Table */}
          <Card sx={{ 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <TableContainer sx={{ height: 'calc(100vh - 450px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < modules.length}
                        checked={modules.length > 0 && selected.length === modules.length}
                        onChange={handleSelectAllClick}
                        inputProps={{ 'aria-label': 'select all modules' }}
                      />
                    </TableCell>
                    <TableCell>Module Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sort Order</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedModules.map((module) => {
                    const isItemSelected = isSelected(module.id);
                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, module.id)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={module.id}
                        selected={isItemSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ 'aria-labelledby': `module-checkbox-${module.id}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{module.display_name}</TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>
                          {module.description || 'No description'}
                        </TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>
                          {module.route || 'No route'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            backgroundColor: module.is_active ? '#dcfce7' : '#fef2f2',
                            color: module.is_active ? '#166534' : '#991b1b',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            <Box sx={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              backgroundColor: getModuleStatusColor(module.is_active) 
                            }} />
                            {module.is_active ? 'Active' : 'Inactive'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>
                          {module.sort_order}
                        </TableCell>
                        <TableCell>{new Date(module.created_at).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              sx={{ mr: 1 }}
                              onClick={(e) => { e.stopPropagation(); navigate(`/modules/edit/${module.id}`); }}
                            >
                              Update
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDeleteModule(module.id); }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={8} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredModules.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: 3,
                  paddingRight: 3,
                },
              }}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Modules;
